import { useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { DaaluuHouseTransfer } from '@big2/shared';

interface Point { x: number; y: number }
interface Route { key: string; from: Point; to: Point; count: number }

/** A surplus holder offers one of their captured houses to a short player. */
export function DaaluuHouseDrag({ children, sellerSeat, buyerSeats, pileId, buyerNames, mn, onOffer }: {
  children: ReactNode; sellerSeat: number; buyerSeats: number[]; pileId: string; buyerNames: string[]; mn: boolean;
  onOffer: (buyerSeat: number, pileId: string) => void;
}) {
  const drag = useRef<{ pointerId: number; x: number; y: number; active: boolean } | null>(null);
  const suppressClick = useRef(false);
  const [ghost, setGhost] = useState<Point | null>(null);
  const [overBuyer, setOverBuyer] = useState(false);

  function buyerAt(x: number, y: number) {
    return buyerSeats.find(seat => {
      const rect = document.querySelector(`[data-daaluu-collection-seat="${seat}"] .daaluu-seat-captures`)?.getBoundingClientRect();
      return !!rect && x >= rect.left - 12 && x <= rect.right + 12 && y >= rect.top - 12 && y <= rect.bottom + 12;
    });
  }
  function release(event?: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    drag.current = null; setGhost(null); setOverBuyer(false);
    if (event && current && event.currentTarget.hasPointerCapture?.(current.pointerId))
      event.currentTarget.releasePointerCapture(current.pointerId);
    return current;
  }
  function start(event: PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0 || drag.current) return;
    suppressClick.current = false;
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, active: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }
  function move(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (!current.active && Math.hypot(event.clientX - current.x, event.clientY - current.y) < 8) return;
    current.active = true; suppressClick.current = true;
    event.preventDefault();
    setGhost({ x: event.clientX, y: event.clientY });
    setOverBuyer(buyerAt(event.clientX, event.clientY) !== undefined);
  }
  function finish(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const accepted = current.active ? buyerAt(event.clientX, event.clientY) : undefined;
    release(event);
    if (accepted !== undefined) onOffer(accepted, pileId);
  }
  return <>
    <button type="button" className={`daaluu-house-seller${ghost ? ' is-dragging' : ''}`}
      data-daaluu-seller-house={sellerSeat}
      aria-label={mn ? `Гэрээ ${buyerNames[0]} рүү өгөх${buyerNames.length > 1 ? ', өөр тоглогч руу чирж болно' : ''}`
        : `Offer a house to ${buyerNames[0]}${buyerNames.length > 1 ? '; drag to choose another player' : ''}`}
      onPointerDown={start} onPointerMove={move} onPointerUp={finish} onPointerCancel={event => { suppressClick.current = true; release(event); }}
      onClick={event => { if (suppressClick.current) { event.preventDefault(); suppressClick.current = false; } else if (buyerSeats.length) onOffer(buyerSeats[0], pileId); }}>
      {children}
    </button>
    {ghost && createPortal(<span className={`daaluu-house-drag-ghost${overBuyer ? ' is-over-buyer' : ''}`} aria-hidden="true"
      style={{ left: ghost.x, top: ghost.y }}>{children}</span>, document.body)}
  </>;
}

/** Settlement paths stay on the felt until the server starts a new round. */
export function DaaluuHouseGuides({ transfers }: { transfers: DaaluuHouseTransfer[] }) {
  const svg = useRef<SVGSVGElement | null>(null);
  const [routes, setRoutes] = useState<Route[]>([]);
  const transferKey = transfers.map(transfer => `${transfer.pileId}:${transfer.fromSeat}:${transfer.toSeat}`).join('|');
  useLayoutEffect(() => {
    const scene = svg.current?.closest('.daaluu-table-scene');
    if (!scene || !transfers.length) { setRoutes([]); return; }
    const measure = () => {
      const bounds = scene.getBoundingClientRect();
      const unique = new Map<string, { fromSeat: number; toSeat: number; count: number }>();
      for (const transfer of transfers) {
        const key = `${transfer.fromSeat}:${transfer.toSeat}`;
        const route = unique.get(key);
        if (route) route.count++;
        else unique.set(key, { fromSeat: transfer.fromSeat, toSeat: transfer.toSeat, count: 1 });
      }
      setRoutes([...unique].flatMap(([key, route]) => {
        const from = scene.querySelector(`[data-daaluu-collection-seat="${route.fromSeat}"] .daaluu-seat-captures`)?.getBoundingClientRect();
        const to = scene.querySelector(`[data-daaluu-collection-seat="${route.toSeat}"] .daaluu-seat-captures`)?.getBoundingClientRect();
        if (!from || !to) return [];
        return [{ key, count: route.count,
          from: { x: from.left + from.width / 2 - bounds.left, y: from.top + from.height / 2 - bounds.top },
          to: { x: to.left + to.width / 2 - bounds.left, y: to.top + to.height / 2 - bounds.top } }];
      }));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) observer.observe(scene);
    return () => { window.removeEventListener('resize', measure); observer?.disconnect(); };
  }, [transferKey]);
  if (!transfers.length) return null;
  return <svg ref={svg} className="daaluu-house-transfer-guides" width="100%" height="100%" aria-hidden="true">
    <defs><marker id="daaluu-house-guide-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto" markerUnits="strokeWidth">
      <path d="M0 0 L7 3.5 L0 7Z" />
    </marker></defs>
    {routes.map(route => {
      const dx = route.to.x - route.from.x, dy = route.to.y - route.from.y;
      const length = Math.hypot(dx, dy) || 1;
      const end = { x: route.to.x - dx / length * 22, y: route.to.y - dy / length * 22 };
      return <g key={route.key}>
        <line className="daaluu-house-transfer-guide" x1={route.from.x} y1={route.from.y} x2={end.x} y2={end.y}
          markerEnd="url(#daaluu-house-guide-arrow)" />
        {route.count > 1 && <text className="daaluu-house-transfer-count" x={(route.from.x + end.x) / 2} y={(route.from.y + end.y) / 2 - 7}
          textAnchor="middle">×{route.count}</text>}
      </g>;
    })}
  </svg>;
}
